/**
 * `request_confirmation` — the single entry point for every gateway mutation,
 * and the approval gate for the kinds that genuinely need a human on the
 * button.
 *
 * BETA-28 (AGENT-DESIGN section 3, audit rule 5) replaced the blanket posture
 * -- "you can NEVER mutate gateway state directly" -- with an explicit list.
 * Every user of this gateway is a Battex engineer who owns the plant, so a card
 * in front of every change was friction, not safety. A card is raised only for
 * `GATED_KINDS`:
 *
 *   - anything the edge signs: every edge connector preview / confirm / retry /
 *     lifecycle / rollback, plus the two composite Modbus client flows that end
 *     in a signed revision. The signed-revision model is non-negotiable.
 *   - PLC lifecycle, fleet rollouts, deployment signing and managed key
 *     rotation. No mutation kind exposes these today -- they stay human-only in
 *     the reviewed GUI -- but any kind added for them belongs on this list.
 *   - user, group and invite administration: it changes who can reach the
 *     plant at all.
 *
 * Everything else is `UNGATED_KINDS` and runs immediately, in the same turn,
 * through `dispatchUngated`. The model gets the real result instead of ending
 * its turn on a card the engineer would have approved anyway.
 *
 * Gated flow: register the proposed action in the pending-action registry and
 * return PENDING_CONFIRMATION. The agent loop sees that error code, stops the
 * turn, and the popover renders an inline approval card. Approve runs the
 * mutation through mutationDispatcher and feeds the result back as a follow-up.
 *
 * Mutation kinds (must match `mutationDispatcher.runKind`). [G] = gated.
 *
 *   submit_open_form               clicks Save on the visible form
 *   delete_datasource              {name}
 *   delete_api_profile             {id}
 *   delete_credential              {id}
 *   delete_oauth_app               {provider}
 *   delete_mqtt_broker             {id}
 *   delete_mqtt_subscription       {id}
 *   delete_historian_provider      {id}
 *   delete_project                 {id}
 *   delete_ai_provider             {id}
 *   set_default_ai_provider        {id}
 *   set_tags_historian              {tag_paths[] | folder_path, enabled?, historian_mode?, historian_interval_ms?}
 *   update_workspace_settings      {homepageRedirect?, runtimeAccess?}
 *   update_my_profile              {first_name?, last_name?, email?, phone?, location?, department?, avatar?}
 *   acknowledge_alarm              {id | alarm_id}
 *   delete_alarm                   {id | alarm_id}
 *   update_alarm                   {id | alarm_id, ...patch}
 * [G] delete_user                  {id}
 * [G] update_user_role             {id, role: 'admin'|'user'|'viewer'}
 * [G] create_invite                {email, role}
 * [G] revoke_invite                {id}
 * [G] delete_group                 {id}
 * [G] add_group_members            {id, userIds: [string]}
 * [G] remove_group_members         {id, userIds: [string]}
 * [G] preview_edge_modbus_client   {gateway_id, device{kind,name,settings,id?}, points[] OR points_csv+tag_folder, expected_revision?}
 * [G] apply_device_template        {gateway_id, template, name, host|serial_port, port?, unit_id, tag_folder?, poll_interval_ms?, historian_enabled?, historian_mode?, historian_interval_ms?}
 * [G] preview_edge_connector       {gateway_id, connector_spec, expected_revision?}
 * [G] confirm_edge_connector       {gateway_id, connector_id, revision, sha256, expected_revision}
 * [G] retry_edge_connector         {gateway_id, connector_id}
 * [G] preview_edge_connector_lifecycle {gateway_id, connector_id, desired_state}
 * [G] preview_edge_connector_rollback  {gateway_id, connector_id, revision}
 */

import { register } from "../pendingActionRegistry";
import { dispatchUngated } from "../mutationDispatcher";

/**
 * The gated list. A kind here raises an approval card and ends the turn.
 * Keep it short and keep the reason with it -- AGENT-DESIGN section 3 decided
 * that anything not on this list runs directly, so an addition here is a
 * deliberate policy change, not a default.
 */
export const GATED_KINDS = new Set([
  // Who can reach the gateway at all (audit rows 5, decision 7.5).
  "delete_user",
  "update_user_role",
  "create_invite",
  "revoke_invite",
  "delete_group",
  "add_group_members",
  "remove_group_members",
  // Anything the edge signs. Preview allocates an immutable revision; applying
  // it always takes a second, digest-bound confirmation (audit rows 11, 16, 17).
  "preview_edge_modbus_client",
  "apply_device_template",
  "preview_edge_connector",
  "confirm_edge_connector",
  "retry_edge_connector",
  "preview_edge_connector_lifecycle",
  "preview_edge_connector_rollback",
  // PLC lifecycle, fleet rollouts, deployment signing and managed key rotation
  // expose no mutation kind today: they are human-only in the reviewed GUI
  // (audit rows 12-19). A kind added for any of them belongs here.
]);

/**
 * Everything else. These run immediately through `dispatchUngated` and hand
 * the model the real result in the same turn.
 */
export const UNGATED_KINDS = new Set([
  "submit_open_form",
  "delete_datasource",
  "delete_api_profile",
  "delete_credential",
  "delete_oauth_app",
  "delete_mqtt_broker",
  "delete_mqtt_subscription",
  "delete_historian_provider",
  "delete_project",
  "delete_ai_provider",
  "set_default_ai_provider",
  "set_tags_historian",
  "update_workspace_settings",
  "update_my_profile",
  "acknowledge_alarm",
  "delete_alarm",
  "update_alarm",
]);

export const VALID_KINDS = new Set([...GATED_KINDS, ...UNGATED_KINDS]);

export const isGatedKind = (kind) => GATED_KINDS.has(kind);

/**
 * Required mutation args per kind. Validated UPFRONT — the previous design
 * registered the proposal regardless of args, then failed inside
 * mutationDispatcher AFTER the user clicked Approve. The user wasted a
 * click and the model lied about success. This catches it before the card
 * is even shown so the model can fix its args.
 */
export const REQUIRED_FIELDS = {
  // The kinds below take `id` OR `alarm_id`, `tag_paths` OR `folder_path`,
  // or need at least one field to change -- neither shape is expressible as
  // a flat required list, so the dispatcher validates them and, being
  // ungated, reports back in the same turn.
  delete_historian_provider: ["id"],
  acknowledge_alarm: [],
  delete_alarm: [],
  update_alarm: [],
  update_my_profile: [],
  update_workspace_settings: [],
  submit_open_form: [],
  set_tags_historian: [],
  delete_datasource: ["name"],
  delete_api_profile: ["id"],
  delete_credential: ["id"],
  delete_oauth_app: ["provider"],
  delete_mqtt_broker: ["id"],
  delete_mqtt_subscription: ["id"],
  delete_user: ["id"],
  update_user_role: ["id", "role"],
  create_invite: ["email", "role"],
  revoke_invite: ["id"],
  delete_group: ["id"],
  add_group_members: ["id", "userIds"],
  remove_group_members: ["id", "userIds"],
  delete_project: ["id"],
  delete_ai_provider: ["id"],
  set_default_ai_provider: ["id"],
  preview_edge_modbus_client: ["gateway_id", "device"],
  apply_device_template: ["gateway_id", "template", "name", "unit_id"],
  preview_edge_connector: ["gateway_id", "connector_spec"],
  confirm_edge_connector: ["gateway_id", "connector_id", "revision", "sha256", "expected_revision"],
  retry_edge_connector: ["gateway_id", "connector_id"],
  preview_edge_connector_lifecycle: ["gateway_id", "connector_id", "desired_state"],
  preview_edge_connector_rollback: ["gateway_id", "connector_id", "revision"],
};

export const request_confirmation = {
  name: "request_confirmation",
  description:
    "Run a gateway mutation. MOST KINDS RUN IMMEDIATELY and return the result in this same " +
    "turn — submitting a pre-filled form (`submit_open_form`), deleting a datasource / API profile / " +
    "credential / OAuth app / MQTT broker or subscription / historian provider / project / AI provider, " +
    "setting the default AI provider, alarm acknowledge / update / delete, profile and Gateway Settings " +
    "edits, and bulk tag history. Do not ask the user first; just do it and report what happened. " +
    "GATED KINDS raise an inline Approve/Reject card, END your turn, and re-invoke you with the " +
    "outcome: user, group and invite administration (`delete_user`, `update_user_role`, `create_invite`, " +
    "`revoke_invite`, `delete_group`, `add_group_members`, `remove_group_members`) and everything the " +
    "edge signs (`preview_edge_modbus_client`, `apply_device_template`, `preview_edge_connector`, " +
    "`confirm_edge_connector`, `retry_edge_connector`, `preview_edge_connector_lifecycle`, " +
    "`preview_edge_connector_rollback`). " +
    "Use a clear, short `title` (e.g. \"Delete connection\"), a `target` identifying what's affected " +
    "(e.g. \"'testdb'\"), `bullets` for the relevant fields, and `warnings` for " +
    "anything irreversible (deletions, role downgrades). For a gated kind the card shows them; for a " +
    "direct kind they are the record of what you did. " +
    "Mutation kinds: submit_open_form, delete_datasource{name}, delete_api_profile{id}, " +
    "delete_credential{id}, delete_oauth_app{provider}, delete_mqtt_broker{id}, " +
    "delete_mqtt_subscription{id}, delete_user{id}, update_user_role{id,role}, " +
    "create_invite{email,role}, revoke_invite{id}, delete_group{id}, " +
    "add_group_members{id,userIds}, remove_group_members{id,userIds}, " +
    "delete_project{id}, delete_ai_provider{id}, set_default_ai_provider{id}, " +
    "delete_historian_provider{id}, " +
    "acknowledge_alarm{id|alarm_id}, delete_alarm{id|alarm_id}, " +
    "update_alarm{id|alarm_id, name?,mode?,setpoint?,setpoint_high?,severity?,message?,on_delay_ms?,off_delay_ms?,enabled?,notify_all?,notify_user_ids?,notify_group_ids?}, " +
    "update_my_profile{first_name?,last_name?,email?,phone?,location?,department?,avatar?} — never a password, " +
    "update_workspace_settings{homepageRedirect?,runtimeAccess?}, " +
    "preview_edge_modbus_client{gateway_id,device{kind,name,settings,id?},points|points_csv,tag_folder?,expected_revision?} — " +
    "full Modbus TCP/RTU CLIENT device with ALL points in one reviewed pass; prefer points_csv rows " +
    "'name,addr,type[,div[,fc[,rw[,pollMs]]]]' copied from the operator's table. Types: U16/UINT16, S16/INT16, U32/UINT32, S32/INT32, F32/FLOAT32, BOOL/BOOLEAN (case-insensitive). " +
    "An 'rw' cell makes the point writable (FC3 read / FC6 write, and its cloud tag is created writable); fc5/fc6/fc15/fc16 imply rw. " +
    "Missing cloud tags are created and declared to the runtime automatically, and a stale expected_revision is refreshed once, " +
    "so pass device.id + expected_revision (from get_edge_connectors) when editing an existing connection, " +
    "apply_device_template{gateway_id,template,name,host|serial_port,port?,unit_id,tag_folder?,poll_interval_ms?,historian_enabled?,historian_mode?,historian_interval_ms?} — " +
    "connect ONE device of a model already saved as a device template (list them with list_device_templates; `template` is its name or id). " +
    "This is the whole job in one approval — the BETA-53 'Agregar dispositivo' flow: the saved register map is instantiated for this host and device address, its tags are created and " +
    "declared to the runtime, the revision is previewed, confirmed, watched until the runtime reports it in sync, AND (unless historian_enabled is false) history is turned on " +
    "for every tag this call just created — no separate set_tags_historian call needed. historian_enabled defaults to true; when omitted, historian_mode defaults to 0 " +
    "(on-change — no interval to pick, and the right default for slow, easily-overloaded hardware like a Solis inverter). Only pass historian_mode 1|2 with historian_interval_ms " +
    "(>=500) if the operator specifically asked for periodic sampling — never invent a fast interval on your own. " +
    "Prefer it over preview_edge_modbus_client whenever a template already describes the device — the points are not re-authored, so nothing is guessed. " +
    "Give each physical device its own tag_folder, " +
    "set_tags_historian{tag_paths|folder_path,enabled?,historian_mode,historian_interval_ms} — bulk-enable tag history after a connector applies. " +
    "Pass EITHER tag_paths — an array of up to 64 FULL tag paths as returned by list_tags' full_path field, e.g. " +
    "[\"default/solis/power_kw\", \"default/solis/energy_today_kwh\"] — OR folder_path, a single folder path " +
    "(e.g. \"default/solis\"); the dispatcher expands folder_path to every tag under it (nested folders included) itself, " +
    "the same way list_tags_by_prefix does, and errors if that resolves to 0 or more than 64 tags. Prefer folder_path " +
    "when the user asks to enable history on 'the whole default/solis folder' — no need to call list_tags first, " +
    "one call finishes the job, " +
    "preview_edge_connector{gateway_id,connector_spec,expected_revision?}, " +
    "confirm_edge_connector{gateway_id,connector_id,revision,sha256,expected_revision}, " +
    "retry_edge_connector{gateway_id,connector_id}, " +
    "preview_edge_connector_lifecycle{gateway_id,connector_id,desired_state}, " +
    "preview_edge_connector_rollback{gateway_id,connector_id,revision}.",
  parameters: {
    type: "object",
    properties: {
      title: { type: "string", minLength: 1 },
      target: { type: "string" },
      bullets: { type: "array", items: { type: "string" } },
      warnings: { type: "array", items: { type: "string" } },
      mutation: {
        type: "object",
        properties: {
          kind: { type: "string", minLength: 1 },
          name: { type: "string" },
          id: { type: "string" },
          provider: { type: "string" },
          email: { type: "string" },
          role: { type: "string", enum: ["admin", "user", "viewer"] },
          userIds: { type: "array", items: { type: "string" } },
          gateway_id: { type: "string" },
          connector_id: { type: "string" },
          connector_spec: { type: "object" },
          device: {
            type: "object",
            description:
              "preview_edge_modbus_client device: {kind: modbus-tcp|modbus-rtu|modbus-rtu-over-tcp, name, id? (only when editing an existing connection), desired_state?, settings{host,port,unitId,timeoutMs?,serialPort?,baudRate?,parity?,pollIntervalMs?}}.",
            properties: {
              kind: { type: "string" },
              name: { type: "string" },
              id: { type: "string" },
              desired_state: { type: "string", enum: ["running", "stopped"] },
              settings: { type: "object" },
            },
          },
          points: { type: "array", items: { type: "object" } },
          points_csv: {
            type: "string",
            description:
              "One point per row: name,addr,type[,div[,fc[,rw[,pollMs]]]] — newline or semicolon separated. " +
              "type is U16/UINT16, S16/INT16, U32/UINT32, S32/INT32, F32/FLOAT32 or BOOL/BOOLEAN (case-insensitive); " +
              "div is the datasheet divisor (1 keeps an Integer tag, >1 scales a Number tag by 1/div); " +
              "fc defaults to 4 for numerics and 2 for booleans; an rw cell (or fc5/fc6/fc15/fc16) makes the point writable " +
              "(FC3 read / FC6 write); pollMs overrides device.settings.pollIntervalMs for that row.",
          },
          tag_folder: { type: "string" },
          template: {
            type: "string",
            description:
              "apply_device_template: the saved device template, by name or id, exactly as list_device_templates reports it.",
          },
          host: { type: "string" },
          serial_port: { type: "string" },
          port: { type: "integer", minimum: 1, maximum: 65535 },
          unit_id: {
            type: "integer",
            minimum: 0,
            maximum: 255,
            description:
              "apply_device_template: the device address (unit id / slave address). 0-255 on Modbus TCP, 1-247 on a serial bus.",
          },
          poll_interval_ms: { type: "integer", minimum: 50, maximum: 3600000 },
          tag_paths: { type: "array", items: { type: "string" } },
          folder_path: {
            type: "string",
            description:
              "set_tags_historian only: a folder path (e.g. \"default/solis\") to bulk-enable instead of listing tag_paths — " +
              "expanded server-side to every tag under it, up to 64.",
          },
          // Alarms. The backend's canonical field is `alarm_id`; `id` is accepted too.
          alarm_id: { type: "string" },
          mode: { type: "string", enum: ["above", "below", "between", "outside_range"] },
          setpoint: { type: "number" },
          setpoint_high: { type: "number" },
          severity: { type: "string", enum: ["critical", "high", "medium", "low"] },
          message: { type: "string" },
          on_delay_ms: { type: "integer", minimum: 0 },
          off_delay_ms: { type: "integer", minimum: 0 },
          notify_all: { type: "boolean" },
          notify_user_ids: { type: "array", items: { type: "string" } },
          notify_group_ids: { type: "array", items: { type: "string" } },
          // update_my_profile. Passwords are refused by the dispatcher.
          first_name: { type: "string" },
          last_name: { type: "string" },
          phone: { type: "string" },
          location: { type: "string" },
          department: { type: "string" },
          avatar: { type: "string" },
          // update_workspace_settings.
          homepageRedirect: { type: "string" },
          runtimeAccess: { type: "string", enum: ["public", "private"] },
          enabled: { type: "boolean" },
          historian_enabled: {
            type: "boolean",
            description:
              "apply_device_template only: turns history on for the tags it just created. Defaults to true — pass false to skip it and leave every tag's historian as-is.",
          },
          historian_mode: { type: "integer", minimum: 0, maximum: 2 },
          historian_interval_ms: { type: "integer", minimum: 500 },
          revision: { type: "integer", minimum: 1 },
          sha256: { type: "string", pattern: "^[a-fA-F0-9]{64}$" },
          expected_revision: { type: "integer", minimum: 0 },
          desired_state: { type: "string", enum: ["running", "stopped"] },
        },
      },
    },
    required: ["title", "mutation"],
    additionalProperties: false,
  },
  execute: async (args, ctx) => {
    const { title, target = "", bullets = [], warnings = [], mutation } = args;
    if (!mutation || typeof mutation !== "object" || !mutation.kind) {
      return {
        ok: false,
        error: { code: "INVALID_ARGS", message: "mutation.kind is required." },
      };
    }
    if (!VALID_KINDS.has(mutation.kind)) {
      return {
        ok: false,
        error: {
          code: "UNKNOWN_MUTATION",
          message: `Unknown mutation kind "${mutation.kind}". See the tool description for valid kinds.`,
        },
      };
    }
    // Pre-flight: validate the mutation has the required args BEFORE we
    // show the user an approval card. Otherwise they click Approve and the
    // dispatcher errors with "requires id (string)" — wasted click.
    const required = REQUIRED_FIELDS[mutation.kind] || [];
    const missing = [];
    for (const key of required) {
      const v = mutation[key];
      const isMissing =
        v == null ||
        (typeof v === "string" && v.trim() === "") ||
        (Array.isArray(v) && v.length === 0);
      if (isMissing) missing.push(key);
    }
    if (missing.length > 0) {
      return {
        ok: false,
        error: {
          code: "INVALID_MUTATION_ARGS",
          message:
            `Mutation "${mutation.kind}" needs: ${required.join(", ")}. ` +
            `You're missing: ${missing.join(", ")}. ` +
            `Look up the missing values first (e.g. \`list_ai_providers\` for an id, \`list_users\` for a user id) and re-call request_confirmation with them included in the mutation object — not just in the bullets.`,
          requiredFields: required,
          missingFields: missing,
        },
      };
    }
    // ── The ungated path ──────────────────────────────────────────────
    // Not on the gated list: run it now and hand the model the real result.
    // No card, no turn boundary, no second round-trip.
    if (!GATED_KINDS.has(mutation.kind)) {
      const result = await dispatchUngated(mutation);
      if (!result || result.ok !== true) {
        return {
          ok: false,
          error: {
            code: "APPLY_FAILED",
            message:
              (result && result.error) || `Mutation "${mutation.kind}" failed.`,
          },
        };
      }
      return {
        ok: true,
        data: {
          applied: true,
          gated: false,
          kind: mutation.kind,
          summary: result.summary,
        },
      };
    }

    const popoverId = ctx?.popoverId;
    if (!popoverId) {
      return {
        ok: false,
        error: {
          code: "NO_POPOVER_ID",
          message: "Confirmation needs a popover context — cannot register pending action.",
        },
      };
    }
    const summary = {
      title,
      target,
      bullets: Array.isArray(bullets) ? bullets : [],
      warnings: Array.isArray(warnings) ? warnings : [],
    };
    const token = register({ popoverId, summary, mutation });
    return {
      ok: false,
      error: {
        code: "PENDING_CONFIRMATION",
        message: `Awaiting user approval for "${title}".`,
        confirmationToken: token,
        proposed: { summary, mutation },
        expiresAt: Date.now() + 5 * 60 * 1000,
      },
    };
  },
};

const confirmTools = [request_confirmation];

export default confirmTools;
